import type { ProfilTexte } from '@rosetta/shared';
import { useId } from 'react';
import { ChargementPanneau } from './chargement-panneau';
import { ColonneTexte } from './colonne-texte';
import { MessageIndisponible } from './message-indisponible';

/** What the analysis of the text gives this tab: its profile, or why there is none. */
export type LectureProfil = ProfilTexte | 'chargement' | 'indisponible';

interface VueTexteOrigineProps {
  /** The text the user typed or loaded. */
  texte: string;
  profil: LectureProfil;
}

/** The rows of the card, in the order of A6: a title, and a hint after it for "Voix". */
const LIGNES: ReadonlyArray<{ champ: keyof ProfilTexte; titre: string; precision?: string }> = [
  { champ: 'voix', titre: 'Voix', precision: 'qui parle, à qui' },
  { champ: 'genre', titre: 'Genre' },
  { champ: 'ton', titre: 'Ton' },
  { champ: 'objectif', titre: 'Objectif' },
];

/**
 * Panel of the "Texte d'origine" tab (A6, #10): the text of origin on the left, and on the right
 * "Analyse du texte", what the analysis step of Tahiti says about it: who speaks and to whom,
 * the kind of text, its tone, its objective, word for word. A field Tahiti did not give has no
 * row; with none at all (or a reading that failed) a sober message says the analysis is not
 * available, as A10 does for the control.
 */
export function VueTexteOrigine({ texte, profil }: VueTexteOrigineProps) {
  const idFiche = useId();
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_440px] items-start gap-12">
      <ColonneTexte titre="Texte d'origine" texte={texte} />
      <section aria-labelledby={idFiche} className="min-w-0">
        <h2 id={idFiche} className="font-semibold text-body-lg leading-[1.3]">
          Analyse du texte
        </h2>
        {profil === 'chargement' ? (
          <ChargementPanneau>Chargement de l'analyse du texte...</ChargementPanneau>
        ) : profil === 'indisponible' ? (
          <MessageIndisponible>L'analyse de ce texte n'est pas disponible.</MessageIndisponible>
        ) : (
          <dl className="mt-3 rounded-md border border-gray-200">
            {LIGNES.flatMap(({ champ, titre, precision }) => {
              const valeur = profil[champ];
              if (valeur === undefined) return [];
              return [
                <div key={champ} className="border-gray-100 border-t px-6 py-4 first:border-t-0">
                  <dt className="text-caption leading-[1.4]">
                    <span className="font-bold">{titre}</span>
                    {precision === undefined ? null : (
                      <span className="text-gray-600"> {precision}</span>
                    )}
                  </dt>
                  <dd className="mt-1 text-body text-gray-800 leading-[1.55]">{valeur}</dd>
                </div>,
              ];
            })}
          </dl>
        )}
      </section>
    </div>
  );
}
